import { checkTestHost, flatOmit } from "@/utils";
import { isShowNews1 } from "@/utils/isShowNews1";
import { miQsWeatherDomains } from "@/constants/domain.config";
import { Key } from "react";
import { getCurrentAdScenario } from "../useAdScenario";
import { ContentAdWaterfall, shouldShowWiAdOnly } from "./shared";

interface AccordionAdProps {
  index: number;
  listLength: number;
  list: any[];
}

const isDomainValid = (index: number, listLength: number, list: any[], testDomains: string[]): boolean => {
  const { hostName } = useHostName();
  const isTestHost = checkTestHost(testDomains) || testDomains.includes(hostName);

  if (index === 0 && list[0]?.isExpand) {
    return true;
  }

  return index !== 0 && index % 3 === 0 && index !== listLength - 1 && isTestHost;
};

const GoogleAdWrapper = ({ index, listLength, list }: AccordionAdProps) => {
  const testDomains = flatOmit(domainMap, ["wi"]);
  if (!isDomainValid(index, listLength, list, testDomains)) {
    return null;
  }
  const adType = index === 0 ? 0 : 1;
  const adProps = useGoogleAd1("ad2", adType);
  return <GoogleAd1 dataPosition="position-36" {...adProps} />;
};

export function useAccordionAdCpt(position: string, index: number, list: any[], pathname: string): JSX.Element | null {
  const { hostName, domain } = useHostName();
  const { isTranTabAdScenario } = getCurrentAdScenario();
  const adType = index === 0 ? 0 : 1;

  const googleAd1Ad2Rotating = useGoogleAd1("ad2", Math.floor(index / 3) % 4);
  const googleAd1Ad1Index2 = useGoogleAd1("ad1", 2);
  const googleAd2Ad1Index0 = useGoogleAd2("ad1", 0);
  const yanDexAd2Props = useYanDex("ad2", adType);
  const googleAd2Ad2Props = useGoogleAd2("ad2", adType);
  const googleAd1Ad1Index1 = useGoogleAd1("ad1", 1);

  switch (position) {
    case "position-36":
      if (miQsWeatherDomains.includes(hostName)) {
        return <GoogleAd1 dataPosition="position-36" {...googleAd1Ad2Rotating} />;
      }
      if (isTranTabAdScenario) {
        return <GoogleAd1 dataPosition="position-36" {...googleAd1Ad1Index2} />;
      }
      if ([...domainMap["v-ls"]].includes(hostName)) {
        return <GoogleAd1 dataPosition="position-36" {...googleAd1Ad1Index2} />;
      }
      if (isShowNews1().isValidDismoreChannel) {
        return <GoogleAd2 dataPosition="position-36" {...googleAd2Ad1Index0} />;
      }
      if (dexLinks.includes(domain)) {
        return <YanDexAd dataPosition={position} {...yanDexAd2Props} />;
      }
      if (isShowNews1().validMain) {
        return <GoogleAd2 dataPosition={position} {...googleAd2Ad2Props} />;
      }
      return <GoogleAdWrapper index={index} listLength={list.length} list={list} />;

    case "position-02":
      const wgDomains = [...domainMap["wg"], ...domainMap["miwg"]];
      if (wgDomains1.includes(hostName) && pathname === "/daily") {
        return null;
      }
      if (wgDomains.includes(hostName) && index === 0 && pathname !== "/hourly") {
        return <GoogleAd1 dataPosition={position} {...googleAd1Ad1Index1} />;
      }
      return null;

    default:
      return null;
  }
}

export function useHourlyAdCpt(position: string): JSX.Element | null {
  const { hostName } = useHostName();
  const wiWgMiwgDomains = [...domainMap["wi"], ...domainMap["wg"], ...domainMap["miwg"]];
  const isInWiWgMiwg = checkTestHost(wiWgMiwgDomains) || wiWgMiwgDomains.includes(hostName);
  const newsVisible = isShowNews1().validMain;

  const googleAd1Ad3Index0 = useGoogleAd1("ad3", 0);
  const googleAd1Ad1Index0 = useGoogleAd1("ad1", 0);

  switch (position) {
    case "position-20":
      if (!newsVisible && isInWiWgMiwg) {
        if (wgDomains1.includes(hostName)) {
          return <GoogleAd1 dataPosition={position} {...googleAd1Ad3Index0} />;
        }
        return <GoogleAd1 dataPosition={position} {...googleAd1Ad1Index0} />;
      }
      return null;

    case "position-11":
      return (
        <ContentAdWaterfall
          position={position}
          config={{
            yanDexIndex: 2,
            newsGoogleAd2Index: 2,
            miwgGoogleAd1Index: 1,
            nonWiGoogleAd1Index: 2,
          }}
        />
      );

    default:
      return null;
  }
}

export function useSunMoonAdCpt(position: string, pathname: string): JSX.Element | null {
  const { hostName } = useHostName();
  const shouldShowWgAd = !isShowNews1().validMain && [...domainMap["wg"], ...domainMap["miwg"]].includes(hostName);

  const googleAd1Ad1Index7 = useGoogleAd1("ad1", 7);
  const googleAd1Ad3Index0 = useGoogleAd1("ad3", 0);
  const googleAd1Ad1Index0 = useGoogleAd1("ad1", 0);

  switch (position) {
    case "position-26":
      if (shouldShowWiAdOnly()) {
        return <GoogleAd1 dataPosition={position} {...googleAd1Ad1Index7} />;
      }
      if (wgDomains1.includes(hostName) && pathname === "/sun-moon") {
        return <GoogleAd1 dataPosition={position} {...googleAd1Ad3Index0} />;
      }
      return null;
    case "position-10":
      return (
        <ContentAdWaterfall
          position={position}
          config={{
            yanDexIndex: 3,
            newsGoogleAd2Index: 3,
            miwgGoogleAd1Index: 3,
            nonWiGoogleAd1Index: 3,
          }}
        />
      );

    case "position-25":
      if (wgDomains1.includes(hostName) && pathname === "/sun-moon") {
        return null;
      }
      return shouldShowWgAd ? <GoogleAd1 dataPosition={position} {...googleAd1Ad1Index0} /> : null;

    default:
      return null;
  }
}

export function useNewsAdCpt(position: string, refreshId?: Key | null | undefined): JSX.Element | null {
  const googleAd2Ad2Index0 = useGoogleAd2("ad2", 0);
  const googleAd1Ad2Index0 = useGoogleAd1("ad2", 0);
  const googleAd2Ad2Index1 = useGoogleAd2("ad2", 1);
  const googleAd1Ad2Index1 = useGoogleAd1("ad2", 1);
  const googleAd2Ad2Index2 = useGoogleAd2("ad2", 2);
  const googleAd1Ad2Index2 = useGoogleAd1("ad2", 2);
  const googleAd2Ad2Index3 = useGoogleAd2("ad2", 3);
  const googleAd1Ad2Index3 = useGoogleAd1("ad2", 3);

  switch (position) {
    case "position-41":
      if (isShowNews1().validMain) {
        return <GoogleAd2 dataPosition="position-41" {...googleAd2Ad2Index0} />;
      }
      if (isShowNews1().isIdVls) {
        return <GoogleAd1 dataPosition="position-41" {...googleAd1Ad2Index0} />;
      }
      return null;
    case "position-42":
      if (isShowNews1().validMain) {
        return <GoogleAd2 dataPosition="position-42" {...googleAd2Ad2Index1} />;
      }
      if (isShowNews1().isIdVls) {
        return <GoogleAd1 dataPosition="position-42" {...googleAd1Ad2Index1} />;
      }
      return null;
    case "position-43":
      if (isShowNews1().validMain) {
        return <GoogleAd2 key={refreshId} dataPosition={position} {...googleAd2Ad2Index2} />;
      }
      if (isShowNews1().isIdVls) {
        return <GoogleAd1 key={refreshId} dataPosition={position} {...googleAd1Ad2Index2} />;
      }
      return null;

    case "position-44":
      if (isShowNews1().validMain) {
        return <GoogleAd2 dataPosition={position} {...googleAd2Ad2Index3} />;
      }
      if (isShowNews1().isIdVls) {
        return <GoogleAd1 dataPosition={position} {...googleAd1Ad2Index3} />;
      }
      return null;
    default:
      return null;
  }
}
